import React, { PureComponent, createRef } from 'react';
import { createRoot } from 'react-dom/client';
import PropTypes from 'prop-types';
import cx from 'classnames';

import DayPicker from 'react-day-picker';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import { formatDate } from 'react-day-picker/moment';

import Navbar from './components/Navbar';
import Weekday from './components/Weekday';
import YearMonthForm from './components/YearMonthForm';
import { hasIgnoreClassName, isValidFormat, maskedValue } from './shared';
import { maxDate } from '../DateRangePicker/configs';
import { ISODateFormat, defaultDateFormat, format, yesterday, subDays, toJSDate } from '../../utils/date';

const rootNode = document.getElementById('single-date-picker');

class SingleDatePicker extends PureComponent {
  static propTypes = {
    maxPeriod: PropTypes.number.isRequired,
    unlimitedPeriod: PropTypes.bool,
    maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    onDateChange: PropTypes.func,
    onDateReset: PropTypes.func,
    onDayChange: PropTypes.func,
    onDayPickerHide: PropTypes.func,
    onDayMouseEnter: PropTypes.func,
    selectedFrom: PropTypes.instanceOf(Date),
    selectedDays: PropTypes.oneOfType([
      PropTypes.instanceOf(Date),
      PropTypes.object,
      PropTypes.array,
    ]),
    disabledDays: PropTypes.oneOfType([
      PropTypes.instanceOf(Date),
      PropTypes.object,
      PropTypes.array,
    ]),
    dayPickerConfig: PropTypes.object,
    hasInput: PropTypes.bool,
    inputValue: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
    inputClassName: PropTypes.string,
    overlayPlacement: PropTypes.oneOf(['bottom-left', 'bottom-right', 'top-left', 'top-right']),
    inputDisabled: PropTypes.bool,
    withCalendarIcon: PropTypes.bool,
    dateDisplayFormat: PropTypes.string,
    placeholder: PropTypes.string,
    invokeDateChangeOnValidInput: PropTypes.bool,
  }

  static defaultProps = {
    hasInput: true,
    selectedFrom: undefined,
    inputDisabled: false,
    withCalendarIcon: false,
    overlayPlacement: 'bottom-left',
    dateDisplayFormat: defaultDateFormat,
    inputClassName: 'form-control',
    maxDate: yesterday(),
    onDateChange: () => {},
    onDayChange: () => {},
    onDayPickerHide: () => {},
    onDayMouseEnter: () => {},
  }

  static getDerivedStateFromProps(newProps, oldState) {
    const maxDateTo = maxDate(newProps.maxDate, newProps.unlimitedPeriod);
    const minDateFrom = subDays(maxDateTo, newProps.maxPeriod - 1);

    return {
      ...oldState,
      maxDateTo,
      minDateFrom,
      toMonth: toJSDate(maxDateTo),
      fromMonth: toJSDate(minDateFrom),
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      month: undefined,
      inputValue: this.getInputValue(props.inputValue) || '',
    };
    this.forwardedRef = createRef();
    this.dropElRef = createRef();
  }

  componentDidUpdate(prevProps) {
    const { inputValue } = this.props;
    if (prevProps.inputValue !== inputValue && inputValue !== this.state.inputValue) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        inputValue: this.getInputValue(inputValue),
        month: this.getInputValue(inputValue),
      });
    }
  }

  getInputValue = (date) => {
    if (!date) return '';
    return toJSDate(date);
  };

  customParseDate = (dateStr) => {
    if (isValidFormat(dateStr)) {
      return this.getInputValue(dateStr);
    }
    return undefined;
  };

  handleDateChange = (day, modifiers = {}) => {
    if (modifiers.disabled) return;

    const parsedDate = format(day, ISODateFormat);
    this.props.onDateChange(parsedDate);
  };

  focusDayPickerInput = () => {
    this.dayPickerInputRef.input.focus();
  };

  handleYearMonthChange = (month) => {
    this.setState({ month });
  };

  handleCustomClosePicker = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!this.dayPickerContainerRef.contains(e.target)) {
      this.setState({ month: undefined });
      this.dayPickerInputRef.hideDayPicker();
    }
  };

  handleDayPickerFocus = ({ target }) => {
    const skipDayPickerInputFocus = hasIgnoreClassName(target, {
      ignoreClassName: 'ignore-focus',
      stopClassName: 'single-date-picker',
    });

    if (skipDayPickerInputFocus) return;
    this.focusDayPickerInput();
  };

  handleDayPickerHide = () => {
    this.props.onDayPickerHide(this.state.fromMonth, this.state.toMonth, this.state.inputValue);
  };

  handleDayChange = (day, modifiers, dayPickerInput) => {
    if (day && this.props.invokeDateChangeOnValidInput) {
      this.handleDateChange(day, modifiers);
    }
    this.props.onDayChange(day, this.state.fromMonth, this.state.toMonth, modifiers, dayPickerInput);
  };

  handleInputChange = ({ target: { value } }) => {
    const { inputDisabled, onDateReset } = this.props;

    if (inputDisabled) return;
    if (!value && onDateReset) onDateReset();
    this.setState({ inputValue: maskedValue(value) });
  };

  renderCaptionElement = ({ date, localeUtils }) => (
    <YearMonthForm
      date={date}
      localeUtils={localeUtils}
      fromMonth={this.state.fromMonth}
      toMonth={this.state.toMonth}
      forwardedRef={this.forwardedRef}
      onChange={this.handleYearMonthChange}
      onMenuClose={this.focusDayPickerInput}
    />
  );

  render() {
    const {
      month, inputValue, maxDateTo, minDateFrom,
    } = this.state;
    const {
      selectedDays, disabledDays, inputDisabled,
      hasInput, inputClassName, selectedFrom,
      dayPickerConfig, overlayPlacement, onDayMouseEnter,
      withCalendarIcon, dateDisplayFormat, placeholder,
    } = this.props;

    const disabledDate = () => {
      if (!disabledDays) {
        return {
          after: toJSDate(maxDateTo),
          before: toJSDate(minDateFrom),
        };
      }

      // eslint-disable-next-line no-prototype-builtins
      if (disabledDays.hasOwnProperty('before') || disabledDays.hasOwnProperty('after')) {
        return {
          after: disabledDays.after || toJSDate(maxDateTo),
          before: disabledDays.before || toJSDate(minDateFrom),
        };
      }
      return disabledDays;
    };

    const dayPickerProps = {
      month: month || selectedFrom || this.state.toMonth,
      selectedDays,
      onDayClick: this.handleDateChange,
      onDayMouseEnter,
      disabledDays: disabledDate(),
      fromMonth: this.state.fromMonth,
      toMonth: this.state.toMonth,
      captionElement: this.renderCaptionElement,
      navbarElement: <Navbar onChange={this.handleYearMonthChange} />,
      firstDayOfWeek: 1,
      weekdayElement: <Weekday />,
      showOutsideDays: true,
      ref: this.dropElRef,
      ...(dayPickerConfig || {}),
    };

    return (
      <div
        ref={ref => this.dayPickerContainerRef = ref}
        className={cx('single-date-picker', {
          'with-calendar-icon': withCalendarIcon,
          disabled: hasInput && inputDisabled,
        })}
      >
        {hasInput ? (
          <DayPickerInput
            ref={ref => this.dayPickerInputRef = ref}
            value={inputValue}
            format={dateDisplayFormat}
            formatDate={formatDate}
            parseDate={this.customParseDate}
            placeholder={placeholder || defaultDateFormat}
            keepFocus={false}
            inputProps={{
              className: cx(inputClassName, { disabled: inputDisabled }),
              onChange: this.handleInputChange,
            }}
            classNames={{
              container: 'DayPickerInput',
              overlay: 'DayPickerInput-Overlay',
              overlayWrapper: cx('DayPickerInput-OverlayWrapper', `${overlayPlacement}-overlay`),
            }}
            dayPickerProps={{
              ...dayPickerProps,
              onBlur: e => this.handleCustomClosePicker(e),
              onFocus: this.handleDayPickerFocus,
            }}
            onDayChange={this.handleDayChange}
            onDayPickerHide={this.handleDayPickerHide}
          />
        ) : <DayPicker {...dayPickerProps} />}
        {hasInput && withCalendarIcon && (
          <span
            className="datepicker-calendar-icon"
            onClick={() => !inputDisabled && this.focusDayPickerInput()}
          />
        )}
      </div>
    );
  }
}

if (rootNode) {
  const { period, selectedDate, pickerFieldClass } = rootNode.dataset;

  const onDateChange = date => document.dispatchEvent(
    new CustomEvent('on-date-change', { detail: { date } }),
  );

  document.addEventListener('DOMContentLoaded', () => {
    const root = createRoot(rootNode);
    root.render(
      <SingleDatePicker
        maxPeriod={JSON.parse(period) || 1}
        inputValue={selectedDate}
        inputClassName={pickerFieldClass}
        onDateChange={onDateChange}
      />,
    );
  });
}

export default SingleDatePicker;
